import React from 'react';
import MathText from '../component/common/MathText';
import {
    IMG_TAG_REGEX,
    containsHtmlTag,
    containsImageTag,
    containsMathContent,
    sanitizeDocxHtml,
    renderSanitizedHtml,
    resetRichTextKeyCounter,
} from '../utils/docxRichRender';

// ==== Component chính, chỉ có DUY NHẤT một RichPreviewText ====
const RichPreviewText = ({ text, html }) => {
    const htmlContent = String(html || '');

    // DOCX uu tien render tu HTML de giu dam/gach chan/xuong dong/hinh anh.
    if (containsHtmlTag(htmlContent)) {
        const safeHtml = sanitizeDocxHtml(htmlContent);
        // Bang (<table>) co cau truc long nhau (table/tr/td). Neu cat chuoi tho
        // theo vi tri <img> nhu ben duoi, cac fragment se bi mo/dong table
        // khong khop nhau khi gan rieng le bang dangerouslySetInnerHTML,
        // khien trinh duyet tu "vá" table va day noi  dung ra ngoai bang.
        // => Voi truong hop co table, bat buoc dung DOM-aware renderer
        // (renderSanitizedHtml) de giu nguyen cau truc, khong duoc split chuoi.
        const hasTableStructure = /<table\b/i.test(safeHtml);

        if (hasTableStructure) {
            resetRichTextKeyCounter();
            return renderSanitizedHtml(safeHtml);
        }
        // Neu co toan, render theo khoi de tranh bi cat vo cau truc \begin...\end
        // khi HTML bi tach thanh nhieu text node nho.
        if (containsMathContent(safeHtml) && containsImageTag(safeHtml)) {
            const parts = safeHtml
                .split(IMG_TAG_REGEX)
                .filter((part) => String(part || '').trim() !== '');

            return (
                <>
                    {parts.map((part, index) => {
                        if (containsImageTag(part)) {
                            return <span key={`img-${index}`} dangerouslySetInnerHTML={{ __html: part }} />;
                        }

                        if (containsMathContent(part)) {
                            return <MathText key={`math-${index}`} text={part} />;
                        }

                        return <span key={`html-${index}`} dangerouslySetInnerHTML={{ __html: part }} />;
                    })}
                </>
            );
        }

        if (containsMathContent(safeHtml)) {
            // Co LaTeX thi uu tien parse bang MathText de render cong thuc dung.
            return <MathText text={safeHtml} />;
        }

        return renderSanitizedHtml(safeHtml);
    }

    if (containsMathContent(text || '')) {
        return <MathText text={text || ''} />;
    }

    // Excel hoac truong hop khong co HTML thi fallback ve render text cu.
    return <MathText text={text || ''} />;
};

// Trich xuat so dong/cau tu thong bao loi de gan lai vao preview item.
const extractRowNumberFromError = (errorText) => {
    const input = String(errorText || '').trim();
    if (!input) {
        return null;
    }

    const docxMatch = input.match(/Dòng\/Câu thứ\s+(\d+)/i);
    if (docxMatch) {
        return Number(docxMatch[1]) || null;
    }

    const rowMatch = input.match(/Dòng\s+(\d+)/i);
    if (rowMatch) {
        return Number(rowMatch[1]) || null;
    }

    const questionMatch = input.match(/Câu\s+(\d+)/i);
    if (questionMatch) {
        return Number(questionMatch[1]) || null;
    }

    return null;
};

const isNodeInsideElement = (node, element) => {
    if (!node || !element) {
        return false;
    }

    const candidate = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return Boolean(candidate && element.contains(candidate));
};

const getLeadingVisibleChar = (node) => {
    if (!node) {
        return '';
    }

    if (node.nodeType === Node.TEXT_NODE) {
        return String(node.textContent || '').trimStart().charAt(0) || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const text = String(node.textContent || '').trimStart();
    return text.charAt(0) || '';
};

const CLIPBOARD_BOUNDARY_SPACE = '\u00A0';
const MATH_BOUNDARY_SELECTOR = 'mjx-container, math, .MathJax, .MathJax_Display';

const getTrailingVisibleChar = (node) => {
    if (!node) {
        return '';
    }

    if (node.nodeType === Node.TEXT_NODE) {
        const text = String(node.textContent || '').trimEnd();
        return text.charAt(text.length - 1) || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const text = String(node.textContent || '').trimEnd();
    return text.charAt(text.length - 1) || '';
};

const isWordLikeChar = (char) => /[\p{L}\p{N}]/u.test(String(char || ''));

const nodeContainsMathMarkup = (node) => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }

    if (node.matches(MATH_BOUNDARY_SELECTOR)) {
        return true;
    }

    return Boolean(node.querySelector(MATH_BOUNDARY_SELECTOR));
};

const shouldInsertBoundarySpace = (prevChar, nextChar) => {
    if (!prevChar || !nextChar) {
        return false;
    }

    if (/[\s\u00A0]/.test(nextChar)) {
        return false;
    }

    if (/[.,;:!?]/.test(nextChar)) {
        return false;
    }

    const prevIsWordOrCloseBracket = isWordLikeChar(prevChar) || /[)}\]]/.test(prevChar);
    const nextIsWordOrOpenBracket = isWordLikeChar(nextChar) || /[[({]/.test(nextChar);

    return prevIsWordOrCloseBracket && nextIsWordOrOpenBracket;
};

const getPreviousMeaningfulSibling = (siblings, index) => {
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
        const candidate = siblings[cursor];
        if (!candidate) {
            continue;
        }

        if (candidate.nodeType === Node.TEXT_NODE && String(candidate.textContent || '').trim() !== '') {
            return candidate;
        }

        if (candidate.nodeType === Node.ELEMENT_NODE && String(candidate.textContent || '').trim() !== '') {
            return candidate;
        }
    }

    return null;
};

const hasLeadingBoundaryWhitespace = (node) => {
    if (!node) {
        return false;
    }

    return /^[\s\u00A0]/.test(String(node.textContent || ''));
};

const hasTrailingBoundaryWhitespace = (node) => {
    if (!node) {
        return false;
    }

    if (node.nodeType === Node.TEXT_NODE) {
        return /[\s\u00A0]$/.test(String(node.textContent || ''));
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
        return /[\s\u00A0]$/.test(String(node.textContent || ''));
    }

    return false;
};

const appendBoundarySpaceToTail = (node) => {
    if (!node) {
        return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = `${String(node.textContent || '')}${CLIPBOARD_BOUNDARY_SPACE}`;
        return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
        node.appendChild(document.createTextNode(CLIPBOARD_BOUNDARY_SPACE));
    }
};

const trimLeadingBoundaryWhitespace = (node) => {
    if (!node) {
        return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = String(node.textContent || '').replace(/^[\s\u00A0]+/, '');
        return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
    }

    const queue = Array.from(node.childNodes || []);
    while (queue.length > 0) {
        const current = queue.shift();
        if (!current) {
            continue;
        }

        if (current.nodeType === Node.TEXT_NODE) {
            current.textContent = String(current.textContent || '').replace(/^[\s\u00A0]+/, '');
            return;
        }

        if (current.nodeType === Node.ELEMENT_NODE) {
            queue.unshift(...Array.from(current.childNodes || []));
        }
    }
};

const normalizeMathSpacingInDom = (root) => {
    const walk = (parentNode) => {
        const siblings = Array.from(parentNode.childNodes || []);

        siblings.forEach((node, index) => {
            const prev = getPreviousMeaningfulSibling(siblings, index);
            const prevChar = getTrailingVisibleChar(prev);
            const nextChar = getLeadingVisibleChar(node);
            const hasLeadingSpace = hasLeadingBoundaryWhitespace(node);
            const hasTrailingSpace = hasTrailingBoundaryWhitespace(prev);
            const boundaryTouchesMath = nodeContainsMathMarkup(prev) || nodeContainsMathMarkup(node);

            if (prev && boundaryTouchesMath && shouldInsertBoundarySpace(prevChar, nextChar)) {
                if (!hasLeadingSpace && !hasTrailingSpace) {
                    if (node.nodeType === Node.TEXT_NODE) {
                        node.textContent = `${CLIPBOARD_BOUNDARY_SPACE}${String(node.textContent || '').replace(/^\s+/, '')}`;
                    } else if (node.parentNode) {
                        node.parentNode.insertBefore(document.createTextNode(CLIPBOARD_BOUNDARY_SPACE), node);
                    }
                } else if (hasLeadingSpace && !hasTrailingSpace) {
                    appendBoundarySpaceToTail(prev);
                    trimLeadingBoundaryWhitespace(node);
                }
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                walk(node);
            }
        });
    };

    walk(root);
};

const buildClipboardPayloadFromHtml = (html) => {
    const markup = String(html || '');
    if (!markup) {
        return { text: '', html: '' };
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = markup;
    normalizeMathSpacingInDom(wrapper);
    //  Loại bỏ vỏ MathJax, giữ lại thẻ <math> sạch với namespace chuẩn
    wrapper.querySelectorAll('mjx-container').forEach((mjxNode) => {
        const mathMmlNode = mjxNode.querySelector('math');
        if (mathMmlNode) {
            const cleanMath = mathMmlNode.cloneNode(true);
            // Thiết lập namespace chuẩn để MS Word tự động parser sanieg OMML
            cleanMath.setAttribute('xmlns', 'http://www.w3.org/1998/Math/MathML');
            
            // Thay thế cả cụm mjx-container bằng thẻ <math> thuần
            mjxNode.parentNode.replaceChild(cleanMath, mjxNode);
        }
    });
    return {
        text: String(wrapper.innerText || wrapper.textContent || '').replace(/\u00A0/g, ' ').trim(),
        html: wrapper.innerHTML,
    };
};

const serializeSelectedFragment = (selection) => {
    if (!selection || selection.rangeCount === 0) {
        return null;
    }

    const range = selection.getRangeAt(0);
    const fragment = range.cloneContents();
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);

    return buildClipboardPayloadFromHtml(wrapper.innerHTML);
};

const buildClipboardPayloadFromSelection = (selection, fallbackHtml) => {
    const fragment = serializeSelectedFragment(selection);

    if (fragment && fragment.html) {
        return fragment;
    }

    const fallback = String(fallbackHtml || '');
    return buildClipboardPayloadFromHtml(fallback);
};

const copyTextToClipboard = async (value) => {
    const payload = typeof value === 'string' ? { text: value, html: value } : value;
    const text = String(payload?.text || '');
    const html = String(payload?.html || '');

    if (navigator?.clipboard?.write && window.ClipboardItem) {
        const clipboardItems = {
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([html || text], { type: 'text/html' }),
        };

        await navigator.clipboard.write([new window.ClipboardItem(clipboardItems)]);
        return;
    }

    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (!copied) {
        throw new Error('Copy khong thanh cong');
    }
};

export default function QuizExcelFormatPreview({ result, isChecking, previewMaxHeight = 650 }) {
    const [copyStatus, setCopyStatus] = React.useState('idle');
    const copyResetTimerRef = React.useRef(null);
    const previewBodyRef = React.useRef(null);

    React.useEffect(() => () => {
        if (copyResetTimerRef.current) {
            window.clearTimeout(copyResetTimerRef.current);
        }
    }, []);

    if (isChecking) {
        return (
            <div className="alert alert-secondary mt-3 mb-0" role="status">
                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                Đang kiểm tra định dạng file Quiz...
            </div>
        );
    }

    if (!result) {
        return null;
    }

    const hasErrors = result.errors.length > 0;
    const hasWarnings = result.warnings.length > 0;

    let summaryClass = 'alert-success';
    if (hasErrors) {
        summaryClass = 'alert-danger';
    } else if (hasWarnings) {
        summaryClass = 'alert-warning';
    }

    const previewQuestions = result.questions;
    const documentTitleText = String(result.documentTitle || '').trim();
    const documentTitleHtml = String(result.documentTitleHtml || '').trim();

    const errorsByRow = new Map();
    const globalErrors = [];

    (result.errors || []).forEach((errorText) => {
        const rowNumber = extractRowNumberFromError(errorText);
        if (!rowNumber) {
            globalErrors.push(errorText);
            return;
        }

        const existing = errorsByRow.get(rowNumber) || [];
        existing.push(errorText);
        errorsByRow.set(rowNumber, existing);
    });

    const parsedQuestionRows = new Set(
        previewQuestions
            .map((question) => Number(question.rowNumber) || 0)
            .filter((rowNumber) => rowNumber > 0)
    );

    const unmatchedErrorRows = Array.from(errorsByRow.keys())
        .filter((rowNumber) => !parsedQuestionRows.has(rowNumber))
        .sort((a, b) => a - b);

    const previewItems = [
        ...previewQuestions.map((question) => ({
            itemType: 'question',
            rowNumber: Number(question.rowNumber) || 0,
            question,
            rowErrors: errorsByRow.get(Number(question.rowNumber) || 0) || [],
        })),
        ...unmatchedErrorRows.map((rowNumber, idx) => ({
            itemType: 'error-only',
            rowNumber,
            id: `error-only-${rowNumber}-${idx}`,
            rowErrors: errorsByRow.get(rowNumber) || [],
        })),
    ].sort((a, b) => {
        const left = Number(a.rowNumber) || Number.MAX_SAFE_INTEGER;
        const right = Number(b.rowNumber) || Number.MAX_SAFE_INTEGER;
        if (left !== right) {
            return left - right;
        }
        if (a.itemType === b.itemType) {
            return 0;
        }
        return a.itemType === 'question' ? -1 : 1;
    });

    const buildCopyPayload = () => {
        const root = previewBodyRef.current;

        if (root) {
            return buildClipboardPayloadFromHtml(root.innerHTML);
        }

        return { text: '', html: '' };
    };

    const handleCopyPreview = async (event) => {
        const selection = window.getSelection();
        const root = previewBodyRef.current;
        const hasSelectionInsidePreview = Boolean(
            selection
            && selection.rangeCount
            && root
            && (isNodeInsideElement(selection.anchorNode, root) || isNodeInsideElement(selection.focusNode, root))
        );

        try {
            if (hasSelectionInsidePreview && event?.clipboardData) {
                event.preventDefault();

                const payload = buildClipboardPayloadFromSelection(selection, root?.innerHTML || '');
                event.clipboardData.setData('text/plain', payload.text || selection.toString());
                event.clipboardData.setData('text/html', payload.html || payload.text || selection.toString());
                return;
            }

            if (event?.type === 'copy' && hasSelectionInsidePreview) {
                event.preventDefault();
                const payload = buildClipboardPayloadFromSelection(selection, root?.innerHTML || '');
                await copyTextToClipboard(payload);
                return;
            }

            await copyTextToClipboard(buildCopyPayload());
            setCopyStatus('copied');

            if (copyResetTimerRef.current) {
                window.clearTimeout(copyResetTimerRef.current);
            }

            copyResetTimerRef.current = window.setTimeout(() => {
                setCopyStatus('idle');
            }, 1800);
        } catch (error) {
            setCopyStatus('error');
        }
    };

    const getSectionKey = (question) => {
        if (question.sectionId !== undefined && question.sectionId !== null) {
            return `section-${question.sectionId}`;
        }

        const title = String(question.sectionTitle || '').trim();
        const content = String(question.sectionContent || '').trim();
        if (!title && !content) {
            return '';
        }

        return `section-fallback-${title}::${content}`;
    };

    return (
        <div className="mt-3">
            <div className={`alert ${summaryClass} mb-2`} role="alert">
                <div className="d-flex flex-wrap gap-3">
                    <span><strong>Sheet:</strong> {result.sheetName}</span>
                    <span><strong>Tổng dòng:</strong> {result.stats.totalRows}</span>
                    <span><strong>Hợp lệ:</strong> {result.stats.validRows}</span>
                    <span><strong>Lỗi:</strong> {result.errors.length}</span>
                    <span><strong>Cảnh báo:</strong> {result.warnings.length}</span>
                </div>
            </div>

            {hasErrors && (
                <div className="alert alert-danger mb-2" role="alert">
                    <strong>File chưa đúng format:</strong>
                    <ul className="mb-0 mt-2 ps-3">
                        {result.errors.slice(0, 6).map((error, idx) => (
                            <li key={`error-${idx}`}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            {hasWarnings && (
                <div className="alert alert-warning mb-2" role="alert">
                    <strong>Cảnh báo:</strong>
                    <ul className="mb-0 mt-2 ps-3">
                        {result.warnings.slice(0, 6).map((warning, idx) => (
                            <li key={`warning-${idx}`}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            

            {previewQuestions.length > 0 && (
                <div className="card border">
                    <div className="card-header py-2 d-flex align-items-center justify-content-between gap-2">
                        <strong>Xem toàn bộ câu hỏi ({previewItems.length} mục)</strong>
                        <button
                            type="button"
                            className="btn btn-link p-0 text-primary shadow-none"
                            onClick={handleCopyPreview}
                            disabled={copyStatus === 'copying'}
                            aria-label="Sao chép nội dung"
                            title="Sao chép nội dung"
                        >
                            <i className={`fas ${copyStatus === 'copied' ? 'fa-check' : 'fa-copy'}`}></i>
                        </button>
                    </div>
                    <div
                        ref={previewBodyRef}
                        className="card-body py-2 quiz-excel-preview-body"
                        style={{ maxHeight: `${previewMaxHeight}px`, overflowY: 'auto' }}
                        onCopy={handleCopyPreview}
                    >
                        {(documentTitleText || documentTitleHtml) && (
                            <div className="quiz-docx-title-box border rounded bg-white p-3 mb-3">
                                <RichPreviewText text={documentTitleText} html={documentTitleHtml} />
                            </div>
                        )}
                        {previewItems.map((item, index) => {
                            if (item.itemType === 'error-only') {
                                return (
                                    <div key={item.id} className="mb-2 border rounded p-2 bg-light">
                                        <div>
                                            <strong>Dòng/Câu {item.rowNumber}:</strong> Không thể preview nội dung câu này do lỗi định dạng.
                                        </div>
                                        <div className="alert alert-danger mt-2 mb-0 py-2" role="alert">
                                            <ul className="mb-0 ps-3">
                                                {item.rowErrors.map((errorText, errIdx) => (
                                                    <li key={`error-only-${item.rowNumber}-${errIdx}`}>{errorText}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                );
                            }

                            const question = item.question;
                            const rowErrors = item.rowErrors || [];
                            const sectionKey = getSectionKey(question);
                            const previousQuestionItem =
                                index > 0
                                    ? [...previewItems]
                                        .slice(0, index)
                                        .reverse()
                                        .find((candidate) => candidate.itemType === 'question')
                                    : null;
                            const previousSectionKey = previousQuestionItem
                                ? getSectionKey(previousQuestionItem.question)
                                : '';
                            const shouldShowSection = Boolean(sectionKey) && sectionKey !== previousSectionKey;

                            return (
                            <div key={question.id} className="mb-2">
                                {shouldShowSection && (
                                    <div className="border rounded bg-white p-2 mb-2">
                                        {!!question.sectionTitle && (
                                            <div className="fw-bold">
                                                <RichPreviewText text={question.sectionTitle} html={question.sectionTitleHtml} />
                                            </div>
                                        )}
                                        {!!question.sectionContent && (
                                            <div className="small mt-1">
                                                <RichPreviewText text={question.sectionContent} html={question.sectionContentHtml} />
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!!String(question.sharedContextText || question.sharedContextHtml || '').trim() && (
                                    <div className="border rounded bg-light p-2 mb-2">
                                        <RichPreviewText
                                            text={question.sharedContextText}
                                            html={question.sharedContextHtml}
                                        />
                                    </div>
                                )}
                                <div>
                                    <strong>Câu {question.rowNumber}:</strong>{' '}
                                    <RichPreviewText text={question.question} html={question.questionHtml} />
                                    {/* <span className="badge bg-secondary ms-2">{" * "+formatTypeLabel(question.questionType)}</span> */}
                                </div>
                                {question.questionType !== 'text' && (
                                    <div className="small text-muted">
                                        {(question.options || []).map((option, idx) => {
                                            const letter = String.fromCharCode(65 + idx);
                                            const isCorrectOption = question.correctAnswers.includes(letter);

                                            return (
                                            <div key={`${question.id}-opt-${idx}`}>
                                                <span className={isCorrectOption ? 'fw-bold ' : ''}>
                                                    {isCorrectOption ? '*' : ''}{letter}.{' '}
                                                </span>
                                                {/* Moi option uu tien optionsHtml neu parser DOCX tra ve. */}
                                                <RichPreviewText
                                                    text={option}
                                                    html={Array.isArray(question.optionsHtml) ? question.optionsHtml[idx] : ''}
                                                />
                                            </div>
                                        );})}
                                        <div>
                                            Đáp án đúng: {question.correctAnswers.join(', ')}
                                        </div>
                                    </div>
                                )}
                                
                                {!!String(question.hint || question.hintHtml || '').trim() && (
                                    <div className="small text-muted">
                                        Gợi ý:{' '}
                                        <RichPreviewText text={question.hint} html={question.hintHtml} />
                                    </div>
                                )}
                                {question.questionType === 'text' && (
                                    <div className="small text-muted">
                                        Đáp án mẫu: {question.correctAnswers.join('; ')}
                                    </div>
                                )}
                                {!!question.explanation && (
                                    <div className="small text-muted">
                                        Giải thích:{' '}
                                        {/* Giai thich cung render rich text de dong bo voi cau hoi/option. */}
                                        <RichPreviewText text={question.explanation} html={question.explanationHtml} />
                                    </div>
                                )}
                                {rowErrors.length > 0 && (
                                    <div className="alert alert-danger mt-2 mb-0 py-2" role="alert">
                                        <ul className="mb-0 ps-3">
                                            {rowErrors.map((errorText, errIdx) => (
                                                <li key={`question-${question.id}-error-${errIdx}`}>{errorText}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                        })}
                    </div>
                </div>
            )}

            {globalErrors.length > 0 && (
                <div className="alert alert-danger mt-2 mb-0" role="alert">
                    <strong>Lỗi không xác định dòng/câu:</strong>
                    <ul className="mb-0 mt-2 ps-3">
                        {globalErrors.map((errorText, idx) => (
                            <li key={`global-error-${idx}`}>{errorText}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
